/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Inject, NgZone} from '@angular/core';
import * as _ from 'lodash';
import template from './ad-unit-edit-page.component.html';
import {AppEventBusService} from '../common/events/app-event-bus.service';

import {AdminResourceEditPageBase} from '../common/controller/admin-resource-edit-page-base';
import {AngularModalService} from '../common/modal/angular-modal.service';
import {createObjectTypeItemLink} from '../common/directives/adn-select/adn-select-item-link.util';
import {AdnSelectSearchElement} from '../common/directives/adn-select/adn-select.component';
import {AdUnitCommonsHost, createAdUnitCommons, resetDimensionsBasedOnAdUnitOption} from './ad-unit-commons';
import {AdUnitSizeModalComponent} from './ad-unit-size-modal.component';
import {OBJECT_TYPE} from '../common/constants/object-type';
import {LocalPausing} from '../../components/session/local-pausing';
import {LocalNetworkInfo} from '../../components/session/local-network-info';
import {getCountryName} from '../common/directives/country-select/countries';
import {SearchResource} from '../../components/api/resources/resource/search';
import {TimezonesResource} from '../../components/api/resources/resource/timezones';
import {BlocklistResource} from '../../components/api/resources/resource/blocklist.resource';
import {RulesetResource} from '../../components/api/resources/resource/ruleset.resource';
import {SiteResource} from '../../components/api/resources/resource/site.resource';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {LocalUserProfile} from '../../components/session/local-user-profile';
import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';
import {WINDOW} from '../angular-platform-providers';
import {deferMarkForCheck} from '../common/util/defer-mark-for-check';

@Component({
  standalone: false,
  selector: 'adn-ad-unit-edit-page',
  template
})
export class AdUnitEditPageComponent extends AdminResourceEditPageBase implements AdUnitCommonsHost {
  readonly fullPageFormats = [
    {code: 'TOP_SCROLL', name: 'Top Scroll'},
    {code: 'MID_SCROLL', name: 'Mid Scroll'},
    {code: 'DOUBLE_FULLSCREEN', name: 'Double Fullscreen'}
  ];

  readonly rulesetItemLink = createObjectTypeItemLink('Ruleset');
  readonly blocklistItemLink = createObjectTypeItemLink('Blocklist');
  readonly siteItemLink = createObjectTypeItemLink('Site');

  siteResults: any[] = [];
  networkTimeZone: string;
  isAxPublisher = false;
  canPauseAdUnits = false;
  pureMarketplacePlatform = false;
  isPoliticalAdsPlatform = false;
  showFullPageOptions = false;
  showCountries = false;
  paused = false;
  politicalAdvertising: 'BLOCKED' | 'UNBLOCKED' = 'UNBLOCKED';
  specialDimensions = false;

  timeZones: any[] | undefined;
  specifyTimeZone = false;
  timeZoneChange?: () => void;
  targetingTypes: any[] = [];
  blocklists: any[] | false = false;
  rulesets: any[] | false = false;
  siteRulesets: any[] = [];
  targetingRulesets: any[] = [];
  sizeRulesets: any[] = [];
  layoutRulesets: any[] = [];

  readonly externalAdUnitsHook: {
    model: any;
    editsMade: () => void;
    initExtAdUnits?: () => void;
    beforeSubmitExtAdUnit?: () => void;
  };

  private readonly commons: ReturnType<typeof createAdUnitCommons>;
  private prevSiteId: string | undefined;
  private pendingAsyncLoads = 0;

  constructor(
    eventBus: AppEventBusService,
    @Inject(UserProfileSaverService) UserProfileSaver: any,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    @Inject(LocalUserProfile) private readonly localUserProfile: any,
    @Inject(SiteResource) private readonly Site: any,
    @Inject(BlocklistResource) private readonly Blocklist: any,
    @Inject(RulesetResource) private readonly Ruleset: any,
    @Inject(LocalUserPermissions) LocalUserPermissions: any,
    timezonesResource: TimezonesResource,
    @Inject(WINDOW) private readonly window: any,
    private readonly searchResource: SearchResource,
    private readonly angularModalService: AngularModalService,
    readonly cdr: ChangeDetectorRef,
    ngZone: NgZone
  ) {
    super(eventBus, UserProfileSaver, LocalNetworkProfile, ngZone);

    this.networkTimeZone = LocalNetworkProfile.get('timeZone');
    this.isAxPublisher = LocalUserPermissions.getAnyVisibility().AX_PUBLISHER === true;
    this.canPauseAdUnits = LocalPausing.obtain().get();
    this.pureMarketplacePlatform = LocalNetworkProfile.isPureMarketplacePlatform();
    this.isPoliticalAdsPlatform = LocalNetworkProfile.isPoliticalAdsPlatform();
    this.showCountries = LocalNetworkInfo.obtain().getSiteCountries().length > 1;

    const isSpecialUser = localUserProfile.get('username') === 'adnuntius@adnuntius.com'
      || localUserProfile.get('username') === 'broker1@adnuntius.com';
    const isLocalhost = this.window.location.hostname === 'localhost';
    this.showFullPageOptions = isLocalhost || (this.pureMarketplacePlatform && isSpecialUser);

    this.commons = createAdUnitCommons(this, timezonesResource, LocalNetworkProfile, 'editingModel');
    this.commons.setTargetingTypes();
    this.commons.setTimezoneChange();

    this.externalAdUnitsHook = {
      model: this.editingModel,
      editsMade: () => this.markExternalFormDirty()
    };

    this.deferFormDirtyTracking();
    this.pendingAsyncLoads = this.pureMarketplacePlatform ? 2 : 1;
    this.loadBlocklists();
    if (this.pureMarketplacePlatform) {
      this.loadRulesets();
    }
  }

  get politicalAdvertisingBlocked(): boolean {
    return this.politicalAdvertising === 'BLOCKED';
  }

  set politicalAdvertisingBlocked(blocked: boolean) {
    this.politicalAdvertising = blocked ? 'BLOCKED' : 'UNBLOCKED';
    this.markExternalFormDirty();
  }

  protected afterInitEditingModel(): void {
    this.externalAdUnitsHook.model = this.editingModel;
    this.commons.setTimezones();
    this.runAfterInitAfterSave();
  }

  protected afterInitAfterSave(): void {
    this.runAfterInitAfterSave();
    if (!this.isNew && this.externalAdUnitsHook.initExtAdUnits) {
      this.externalAdUnitsHook.initExtAdUnits();
    }
  }

  protected onBeforeSubmit(): void {
    if (this.editingModel.politicalAdvertising !== this.politicalAdvertising) {
      if (this.politicalAdvertising === 'BLOCKED') {
        this.editingModel.politicalAdvertising = 'BLOCKED';
      } else {
        this.editingModel.politicalAdvertising = 'ALLOWED';
      }
    }
    this.editingModel.timeZone = this.commons.getBeforeSubmitTimeZoneValue();
    resetDimensionsBasedOnAdUnitOption(this.editingModel);

    if (!this.specialDimensions) {
      this.editingModel.fullPageWidth = 0;
      this.editingModel.fullPageHeight = 0;
      this.editingModel.mobileFullPageWidth = 0;
      this.editingModel.mobileFullPageHeight = 0;
    }

    if (this.externalAdUnitsHook.beforeSubmitExtAdUnit) {
      this.externalAdUnitsHook.beforeSubmitExtAdUnit();
    }
  }

  addDimensions(): void {
    this.editingModel.dimensions = this.editingModel.dimensions || [];
    this.editingModel.dimensions.push([]);
    this.markExternalFormDirty();
  }

  removeDimensions(index: number): void {
    _.pullAt(this.editingModel.dimensions, index);
    this.markExternalFormDirty();
  }

  previewSize(): void {
    this.angularModalService.show(AdUnitSizeModalComponent, {
      class: 'modal-lg',
      initialState: {
        minWidth: _.isFinite(this.editingModel.minWidth) ? this.editingModel.minWidth : 0,
        minHeight: _.isFinite(this.editingModel.minHeight) ? this.editingModel.minHeight : 0,
        width: _.isFinite(this.editingModel.width) ? this.editingModel.width : 0,
        height: _.isFinite(this.editingModel.height) ? this.editingModel.height : 0
      }
    });
  }

  onTimeZoneModeChange(): void {
    if (this.timeZoneChange) {
      this.timeZoneChange();
    }
    this.markExternalFormDirty();
  }

  siteSearch = (searchElement: AdnSelectSearchElement): void => {
    this.searchResource.query({q: searchElement.search, types: 'Site', pageSize: 50}).then((data: any) => {
      this.ngZone.run(() => {
        this.siteResults = data.searchResults;
      });
    });
  };

  private runAfterInitAfterSave(): void {
    this.editingModel.countryExpanded = getCountryName(this.editingModel.country);

    if (this.prevSiteId !== _.get(this.editingModel, ['site', 'id']) && this.editingModel.site?.id) {
      this.Site.get({id: this.editingModel.site.id}, (site: any) => {
        this.ngZone.run(() => {
          this.editingModel.earningsAccount = site.earningsAccount;
          this.modelComms?.relDataUpdated(this.editingModel.earningsAccount, OBJECT_TYPE.EarningsAccount);
        });
      });
      this.prevSiteId = this.editingModel.site.id;
    }

    this.politicalAdvertising = 'BLOCKED';
    if (!this.editingModel.politicalAdvertising || this.editingModel.politicalAdvertising !== 'BLOCKED') {
      this.politicalAdvertising = 'UNBLOCKED';
    }

    this.editingModel.targetingControl = this.editingModel.targetingControl || 'NONE';
    this.editingModel.fullPageFormats = this.editingModel.fullPageFormats || [];
    this.editingModel.allowedTargetTypes = this.editingModel.allowedTargetTypes || [];
    this.editingModel.dimensions = _.isArray(this.editingModel.dimensions) ? this.editingModel.dimensions : [[]];
    this.editingModel.dimensions[0] = _.isArray(this.editingModel.dimensions[0]) ? this.editingModel.dimensions[0] : [];
    this.editingModel.renderOption = this.editingModel.renderOption || null;

    const isFixed = _.get(this.editingModel, ['dimensions', 0, 0]) >= 0
      && _.get(this.editingModel, ['dimensions', 0, 1]) >= 0;
    this.editingModel.adUnitOption = this.editingModel.adUnitOption || (isFixed ? 'FIXED' : 'RANGE');
    this.paused = this.model?.paused;

    this.specialDimensions = !!(
      this.editingModel.fullPageWidth
      || this.editingModel.fullPageHeight
      || this.editingModel.mobileFullPageWidth
      || this.editingModel.mobileFullPageHeight
    );
    if (this.editingModel.fullPageWidth === 0) {
      this.editingModel.fullPageWidth = undefined;
    }
    if (this.editingModel.fullPageHeight === 0) {
      this.editingModel.fullPageHeight = undefined;
    }
    if (this.editingModel.mobileFullPageWidth === 0) {
      this.editingModel.mobileFullPageWidth = undefined;
    }
    if (this.editingModel.mobileFullPageHeight === 0) {
      this.editingModel.mobileFullPageHeight = undefined;
    }

    this.resolveBlocklistReference();
    this.resolveRulesetReferences();
  }

  private loadBlocklists(): void {
    this.Blocklist.query({minimal: true}, (page: any) => {
      // false → [] flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
      deferMarkForCheck(this.cdr, () => {
        this.blocklists = page.results || [];
        this.resolveBlocklistReference();
        this.onAsyncLoadComplete();
      });
    });
  }

  private loadRulesets(): void {
    this.Ruleset.query({minimal: false}, (page: any) => {
      deferMarkForCheck(this.cdr, () => {
        const results = page.results || [];
        this.rulesets = results;
        this.siteRulesets = _.filter(results, (rs: any) => rs.type === 'INVENTORY');
        this.targetingRulesets = _.filter(results, (rs: any) => rs.type === 'TARGETING');
        this.sizeRulesets = _.filter(results, (rs: any) => rs.type === 'SIZE');
        this.layoutRulesets = _.filter(results, (rs: any) => rs.type === 'LAYOUT');
        this.resolveRulesetReferences();
        this.onAsyncLoadComplete();
      });
    });
  }

  private onAsyncLoadComplete(): void {
    this.pendingAsyncLoads--;
    if (this.pendingAsyncLoads <= 0) {
      this.resumeFormDirtyTracking();
    }
  }

  private resolveBlocklistReference(): void {
    if (!_.isArray(this.blocklists) || !this.editingModel?.blocklist?.id) {
      return;
    }
    const match = _.find(this.blocklists, ['id', this.editingModel.blocklist.id]);
    if (match) {
      this.editingModel.blocklist = match;
    }
  }

  private resolveRulesetReferences(): void {
    if (!_.isArray(this.rulesets)) {
      return;
    }
    if (this.editingModel.siteRuleset?.id) {
      const siteRuleset = _.find(this.rulesets, ['id', this.editingModel.siteRuleset.id]);
      if (siteRuleset) {
        this.editingModel.siteRuleset = siteRuleset;
      }
    }
  }
}
